// LegacyLens — Preserve life stories through AI-guided conversations
import "@/App.css";
import React, { useState, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "@/contexts/I18nContext";
import { authAPI } from "@/lib/api";
import seoLanguages from "@/seo/languages.json";
import seoPages from "@/seo/pages.json";

// LandingPageV2 (new default) loaded eagerly; classic lazy-loaded
import LandingPage from "@/pages/LandingPageV2";
const LandingPageClassic = React.lazy(() => import("@/pages/LandingPage"));

// All other pages lazy-loaded for code splitting
const OnboardingPage = React.lazy(() => import("@/pages/OnboardingPage"));
const DashboardPage = React.lazy(() => import("@/pages/DashboardPage"));
const BiographerPage = React.lazy(() => import("@/pages/BiographerPage"));
const TimelinePage = React.lazy(() => import("@/pages/TimelinePage"));
const ReplicaPage = React.lazy(() => import("@/pages/ReplicaPage"));
const SettingsPage = React.lazy(() => import("@/pages/SettingsPage"));
const AdminPage = React.lazy(() => import("@/pages/AdminPage"));
const TermsPage = React.lazy(() => import("@/pages/TermsPage"));
const PaymentPage = React.lazy(() => import("@/pages/PaymentPage"));
const PaymentSuccessPage = React.lazy(() => import("@/pages/PaymentSuccessPage"));
const JournalPage = React.lazy(() => import("@/pages/JournalPage"));
const PeoplePage = React.lazy(() => import("@/pages/PeoplePage"));
const SharedMemoriesPage = React.lazy(() => import("@/pages/SharedMemoriesPage"));
const ResetPasswordPage = React.lazy(() => import("@/pages/ResetPasswordPage"));
const AffiliatePage = React.lazy(() => import("@/pages/AffiliatePage"));
const GiftPage = React.lazy(() => import("@/pages/GiftPage"));
const GiftSuccessPage = React.lazy(() => import("@/pages/GiftSuccessPage"));
const GiftRedeemPage = React.lazy(() => import("@/pages/GiftRedeemPage"));
const FacilityPage = React.lazy(() => import("@/pages/FacilityPage"));
const CorrectiveSessionPage = React.lazy(() => import("@/pages/CorrectiveSessionPage"));
const ContentPage = React.lazy(() => import("@/pages/ContentPage"));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function AgeConfirmationGate({ children }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!user || user.age_confirmed || confirmed) return children;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await authAPI.confirmAge();
      setConfirmed(true);
    } catch {
      setConfirming(false);
    }
  };

  const renderDescription = () => {
    const raw = t('age.description');
    const parts = raw.split(/\{bold\}|\{\/bold\}/);
    return parts.map((part, i) => i === 1 ? <strong key={i}>{part}</strong> : part);
  };

  return (
    <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-[#e7e5e4] p-8 text-center" data-testid="age-confirmation-gate">
        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-[#b45309]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        </div>
        <h2 className="text-xl font-bold text-[#1c1917] mb-2">{t('age.title')}</h2>
        <p className="text-sm text-[#78716c] mb-6 leading-relaxed">
          {renderDescription()}
        </p>
        <button
          onClick={handleConfirm}
          disabled={confirming}
          className="w-full py-3 rounded-full bg-[#b45309] hover:bg-[#92400e] text-white font-medium transition-colors disabled:opacity-50"
          data-testid="age-confirm-btn"
        >
          {confirming ? t('age.confirming') : t('age.confirm')}
        </button>
        <p className="text-xs text-[#a8a29e] mt-4">
          {t('age.footnote')}
        </p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
        <div className="w-8 h-8 border-2 border-[#b45309] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return <AgeConfirmationGate>{children}</AgeConfirmationGate>;
}

function AppRoutes() {
  const { user, profiles, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
        <div className="w-8 h-8 border-2 border-[#b45309] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const loadingFallback = (
    <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
      <div className="w-8 h-8 border-2 border-[#b45309] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <Suspense fallback={loadingFallback}>
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        <Route path="/classic" element={user ? <Navigate to="/dashboard" replace /> : <LandingPageClassic />} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/session/:sessionId" element={<ProtectedRoute><BiographerPage /></ProtectedRoute>} />
        <Route path="/journal" element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
        <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
        <Route path="/people" element={<ProtectedRoute><PeoplePage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
        <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
        <Route path="/affiliate" element={<ProtectedRoute><AffiliatePage /></ProtectedRoute>} />
        <Route path="/facility" element={<ProtectedRoute><FacilityPage /></ProtectedRoute>} />
        <Route path="/corrections" element={<ProtectedRoute><CorrectiveSessionPage /></ProtectedRoute>} />
        <Route path="/gift" element={<GiftPage />} />
        <Route path="/gift/success" element={<GiftSuccessPage />} />
        <Route path="/gift/redeem/:code" element={<GiftRedeemPage />} />
        <Route path="/replica" element={<ReplicaPage />} />
        <Route path="/shared/:token" element={<SharedMemoriesPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {Object.entries(seoPages.pages).filter(([, p]) => p.layout === "guide").map(([route]) => (
          <Route key={route} path={route} element={<ContentPage path={route} />} />
        ))}
        {seoLanguages.languages.filter((l) => l.path !== "/").map((l) => (
          <Route key={l.code} path={l.path} element={user ? <Navigate to="/dashboard" replace /> : <LandingPage lang={l.code} />} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App({ preloadedTranslations }) {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <I18nProvider preloaded={preloadedTranslations}>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </I18nProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
